import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiError, getAuthToken } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { FeedbackTabStrip, type FeedbackTab } from "./FeedbackTabStrip";
import { LinkedPlayersStrip } from "./components/LinkedPlayersStrip";
import { FeedbackPlaylistsTab } from "./FeedbackPlaylistsTab";
import { FeedbackNotesTab } from "./FeedbackNotesTab";
import { FeedbackClipsTab } from "./FeedbackClipsTab";
import { FeedbackSummariesTab } from "./FeedbackSummariesTab";
import { FeedbackDevelopmentTab } from "./FeedbackDevelopmentTab";
import {
  reviewedNoteIds as deriveReviewedNoteIds,
  reviewedPlaylistIds as deriveReviewedPlaylistIds,
} from "./feedbackHelpers";
import { useMyFeedback } from "./hooks/useMyFeedback";

/**
 * Player/guardian feedback shell — the host for the `/feedback`
 * viewer surfaces (Playlists · Notes · Clips · Match Summaries ·
 * Development). Ports the legacy `setFeedbackTab` /
 * `renderMyFeedback` shell from `js/coaching/feedback.js`.
 *
 * Routing: `/feedback?tab=<tab>`; absent ⇒ `playlists` (legacy
 * default). The 5 tabs match the legacy `VALID_FEEDBACK_TABS`.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * `/api/my-feedback` resolves the viewer's team from the request
 * scope, so the scope store must be populated first (the same
 * latent-bug pattern the coach surfaces guard against).
 *
 * `useMyFeedback()` is the single mount-time bundle fetch feeding
 * the list tabs. On a 401 / no-token the shell renders the inline
 * login panel instead of the (empty) tabs.
 *
 * M7 slice 3 ships the shell + routing + the strips + the auth
 * gate. The per-tab list surfaces, the Development profile, and
 * the goal surfaces land in the following M7 slices — each one
 * swaps its placeholder for the real surface.
 */

export function FeedbackShell() {
  // Reconcile auth + scope — `/api/my-feedback` is scope-resolved
  // server-side, so a direct nav must not leave the scope store
  // unpopulated.
  useMe();

  const navigate = useNavigate({ from: "/feedback" });
  const search = useSearch({ from: "/feedback" });
  // `tab` absent ⇒ playlists (legacy default).
  const activeTab: FeedbackTab = search.tab ?? "playlists";

  const feedback = useMyFeedback();
  const bundle = feedback.data;

  // The viewer's reviewed-id sets, derived once from `bundle.reviews`
  // — drive the Reviewed/New pills on the Notes + Playlists cards.
  const reviewedNoteIds = useMemo(
    () => deriveReviewedNoteIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  const reviewedPlaylistIds = useMemo(
    () => deriveReviewedPlaylistIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // M9a A8 — auth gate. A 401 from /api/my-feedback OR no stored
  // token ⇒ redirect to `/login?next=/feedback` (replaces the M4
  // <InlineLoginPanel> that used to render inline).
  const isAuthError =
    feedback.error instanceof ApiError && feedback.error.status === 401;
  const noToken = !getAuthToken();
  const showLogin = isAuthError || (feedback.isError && noToken);

  // Guard against re-fire after the redirect changes the URL —
  // see AdminShell for the full explanation. `next` is always
  // `/feedback` for this shell, so a simple ref-flag is enough.
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: "/feedback" },
      replace: true,
    });
  }, [showLogin, navigate]);

  return (
    <main className="min-h-screen p-6 text-ink" data-testid="feedback-shell">
      <header className="mb-6">
        <h1 className="text-3xl font-display tracking-tight">My Feedback</h1>
        <p className="text-ink-muted text-sm mt-1">
          Your coaching notes, clips, review playlists, and development.
        </p>
      </header>

      {showLogin ? null : (
        <>
          {/* Linked-players chip strip — who's linked to this
           * account (empty ⇒ "ask a coach"). */}
          <div className="mb-4">
            <LinkedPlayersStrip players={feedback.data?.players ?? []} />
          </div>

          {/* The sub-tab strip leads the tab content. */}
          <div className="mb-4">
            <FeedbackTabStrip active={activeTab} />
          </div>

          {/* Non-auth bundle errors surface here. */}
          {feedback.isError && !isAuthError ? (
            <p
              className="text-destructive font-mono text-sm mb-4"
              data-testid="feedback-bundle-error"
            >
              Could not load your feedback:{" "}
              {feedback.error instanceof Error
                ? feedback.error.message
                : "unknown error"}
            </p>
          ) : null}

          <section
            aria-label={TAB_LABELS[activeTab]}
            data-testid={`feedback-panel-${activeTab}`}
          >
            {/* Loading the bundle — show a light placeholder so the
             * panel isn't empty mid-fetch. */}
            {feedback.isLoading ? (
              <p
                className="text-ink-muted text-sm font-mono px-3 py-6"
                data-testid="feedback-loading"
              >
                Loading your feedback…
              </p>
            ) : activeTab === "playlists" ? (
              <FeedbackPlaylistsTab
                playlists={bundle?.playlists ?? []}
                reviewedPlaylistIds={reviewedPlaylistIds}
                players={bundle?.players ?? []}
                reviewedNoteIds={reviewedNoteIds}
              />
            ) : activeTab === "notes" ? (
              <FeedbackNotesTab
                notes={bundle?.notes ?? []}
                reviewedNoteIds={reviewedNoteIds}
                players={bundle?.players ?? []}
              />
            ) : activeTab === "clips" ? (
              <FeedbackClipsTab
                clips={bundle?.clips ?? []}
                players={bundle?.players ?? []}
              />
            ) : activeTab === "summaries" ? (
              <FeedbackSummariesTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
              />
            ) : (
              <FeedbackDevelopmentTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
              />
            )}
          </section>
        </>
      )}
    </main>
  );
}

/** Tab labels — used for the active panel's accessible name.
 * Every tab is a real surface as of M7 slice 7; there's no
 * placeholder fallback. */
const TAB_LABELS: Record<FeedbackTab, string> = {
  playlists: "Playlists",
  notes: "Notes",
  clips: "Clips",
  summaries: "Match Summaries",
  development: "Development",
};
